import { gql } from '@apollo/client';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { US } from '@reverbdotcom/commons/src/constants';
import { CSP } from '../../../../schemas/rql';
import { conditionSlugForUuid } from '@reverbdotcom/commons/src/condition_helpers';
import { 
  PAID_TRANSACTION_STATUSES, 
  SELLER_COUNTRY_CODES, 
} from '../../show/price_guide_tool_transaction_graph_container';

const ORDER_QUERY_OFFSET = 0;
const ORDER_QUERY_LIMIT = 1; // We only query against search to get total records. No need for actual results
interface IFilterProps {
  csp: CSP;
  conditionUuids: string[];
  canonicalProductId?: string;
}

export const CSP_PRICE_REC_QUERY_DEFINITION = gql`
query DataServices_PriceGuide_CspEstimates(
  $cspPriceRecommendationQueries: [Input_reverb_pricing_CSPPriceRecommendationQuery]
) {
  cspPriceRecommendations(input: {
    cspPriceRecommendationQueries: $cspPriceRecommendationQueries
  }) {
    priceRecommendations {
      conditionUuid
      priceLow {
        amountCents
        currency
      }
      priceMiddle {
        amountCents
        currency
      }
      priceHigh {
        amountCents
        currency
      }
      priceLowThirtyDaysAgo {
        amountCents
        currency
      }
      priceMiddleThirtyDaysAgo {
        amountCents
        currency
      }
      priceHighThirtyDaysAgo {
        amountCents
        currency
      }
    }
  }
}
`;

export const withCSPPriceRecQuery = withGraphql<IFilterProps>(
  CSP_PRICE_REC_QUERY_DEFINITION,
  {
    skip: ({ canonicalProductId }) => !!canonicalProductId,
    name: 'cspEstimateData',
    options: ({ 
      csp, 
      conditionUuids,
    }) => {
      const cspPriceRecommendationQueries = conditionUuids.map((conditionUuid) => {
        return {
          comparisonShoppingPageId: csp.id,
          conditionUuid,
          countryCode: US,
        };
      });
  
      return {
        ssr: false,
        fetchPolicy: 'network-only',
        variables: {
          cspPriceRecommendationQueries,
        },
      };
    },
  },
);


export const withCPriceRecQuery = withGraphql<IFilterProps>(
  gql`
    query DataServices_PriceGuide_CpEstimates(
      $priceRecommendationQueries: [Input_reverb_pricing_PriceRecommendationQuery]
    ) {
      priceRecommendations(input: {
        priceRecommendationQueries: $priceRecommendationQueries
      }) {
        priceRecommendations {
          conditionUuid
          priceLow {
            amountCents
            currency
          }
          priceMiddle {
            amountCents
            currency
          }
          priceHigh {
            amountCents
            currency
          }
          priceLowThirtyDaysAgo {
            amountCents
            currency
          }
          priceMiddleThirtyDaysAgo {
            amountCents
            currency
          }
          priceHighThirtyDaysAgo {
            amountCents
            currency
          }
        }
      }
    }
  `,
  {
    skip: ({ canonicalProductId }) => !canonicalProductId,
    name: 'cpEstimateData',
    options: ({ 
      conditionUuids,
      canonicalProductId,
    }) => {
      const priceRecommendationQueries = conditionUuids.map(conditionUuid => {
        return {
          canonicalProductId,
          conditionUuid,
          countryCode: US,
        };
      });
  
      return {
        ssr: false,
        fetchPolicy: 'network-only',
        variables: {
          priceRecommendationQueries,
        },
      };
    },
  },
);

interface ITotalOrderProps {
  csp: CSP;
  conditionUuids: string[];
  canonicalProductId: string;
}

export const withTotalOrdersQuery = withGraphql<ITotalOrderProps>(
  gql`
  query Search_PriceGuide_TotalOrders(
    $canonicalProductIds: [String],
    $sellerCountries: [String],
    $conditionSlugs: [String],
    $actionableStatuses: [String],
    $limit: Int,
    $offset: Int
  ) {
    priceRecordsSearch(input: {
      canonicalProductIds: $canonicalProductIds,
      sellerCountries: $sellerCountries,
      listingConditionSlugs: $conditionSlugs,
      actionableStatuses: $actionableStatuses,
      limit: $limit,
      offset: $offset
    }) {
      total
      offset
    }
  }`, 
  {
    name: 'ordersData',
    options: ({
      csp,
      conditionUuids,
      canonicalProductId,
    }) => {    
      const conditionSlugs = conditionUuids.map(conditionUuid => conditionSlugForUuid(conditionUuid));
      const canonicalProductIds = canonicalProductId ? [canonicalProductId] : csp.variants.map(variant => variant.id);
      
      return {
        ssr: false,
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
        variables: {
          offset: ORDER_QUERY_OFFSET,
          limit: ORDER_QUERY_LIMIT,
          conditionSlugs,
          canonicalProductIds,
          sellerCountries: SELLER_COUNTRY_CODES,
          actionableStatuses: PAID_TRANSACTION_STATUSES,
        },
      };
    },
  },
);
